<script setup>
/** SelectGroup */
/* Use for groups of facets in search/browse UIs:
    - in which all facets appear in a dropdown/HTML <select>
    - and the user can only select one at a time
    Example: "Subject" dropdown in Browse Goals and Browse Present Levels
*/
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/dropdown-menu'
import LockedOption from '@/components/LockedOption.vue'
import { computed } from 'vue'
import { ScrollAreaRoot, ScrollAreaViewport, ScrollAreaScrollbar, ScrollAreaThumb } from 'reka-ui'

const props = defineProps({
  // The group of select options: {label: String, value: String, disabled: Boolean, locked: Boolean, separator: Boolean}
  filterGroup: {
    type: Array,
    default: () => [],
    validator: (value) => Array.isArray(value) && value.every(item => 
      typeof item === 'object' && 
      (item.separator || (typeof item.label === 'string' && typeof item.value === 'string'))
    )
  },
  // Label for the dropdown
  groupLabel: {
    type: String,
    required: true
  },
  // Placeholder value
  placeholder: {
    type: String,
    default: 'Select an option'
  },
  // The selected value. v-model in the parent.
  modelValue: {
    type: String,
    default: null
  },
  // Container portal target for modals
  containerPortal: {
    type: String,
    default: undefined
  },
  showLabel: {
    type: Boolean,
    default: true
  },
  gbTheme: {
    type: String,
    default: 'blue',
    validator: (value) => ['blue', 'purple'].includes(value)
  },
  triggerLabelClasses: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['update:modelValue'])

// Find the selected item's label to display in the trigger
const getSelectedLabel = () => {
  if (!props.modelValue) return props.placeholder
  if (!props.filterGroup || !Array.isArray(props.filterGroup)) return props.placeholder
  const selectedItem = props.filterGroup.find(item => item.value === props.modelValue)
  return selectedItem?.label || props.placeholder
}

const themedBgColor = computed(() => {
  switch (props.gbTheme) {
    case 'blue':
      return 'bg-primary-400'
    case 'purple':
      return 'bg-purple-300'
    default:
      return 'bg-primary-400'
  }
})
</script>

<template>
  <div class="mb-6 mt-2">
    <p v-if="showLabel" class="my-2 font-bold uppercase text-gray-900">{{ groupLabel }}</p>
    <DropdownMenu>
      <DropdownMenuTrigger
        class="font-normal w-full rounded-xs border border-gray-600 bg-white hover:border-black hover:bg-gray-300 flex items-center justify-between px-3 py-[6px] focus-visible:ring text-md"
        :class="{ 'border-red-300': $attrs.class?.includes('error') }"
        :aria-label="groupLabel + ' selection'"
        tabindex="0"
      >
        <span :class="triggerLabelClasses">{{ getSelectedLabel() }}</span>
        <span class="material-icons-round text-display-sm text-gray-900">arrow_drop_down</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent class="w-[--reka-dropdown-menu-trigger-width] p-0" :containerPortal="containerPortal">
        <ScrollAreaRoot class="w-full h-[300px]" type="auto">
          <ScrollAreaViewport class="w-full h-full">
            <template v-for="(item, index) in filterGroup" :key="item.value !== undefined ? item.value : index">
              <DropdownMenuItem
                v-if="item.locked"
                :disabled="true"
                class="py-2 px-4 rounded-none cursor-not-allowed"
              >
                <LockedOption :label="item.label" />
              </DropdownMenuItem>
              <DropdownMenuItem
                v-else
                :disabled="item.disabled"
                @click="emit('update:modelValue', item.value)"
                class="cursor-pointer py-2 px-4 hover:bg-gray-300 focus:bg-gray-300 focus:outline-none rounded-none"
                :class="item.value === props.modelValue ? themedBgColor + ' text-white' : ''"
              >
                {{ item.label }}
              </DropdownMenuItem>
              <DropdownMenuSeparator v-if="item.separator" class="bg-gray-400"/>
            </template>
          </ScrollAreaViewport>
          <ScrollAreaScrollbar orientation="vertical" class="flex select-none touch-none p-0.5 bg-gray-100 transition-colors w-[10px]">
            <ScrollAreaThumb class="flex-1 bg-gray-300 rounded-[10px] relative before:content-[''] before:absolute before:top-1/2 before:left-1/2 before:-translate-x-1/2 before:-translate-y-1/2 before:w-full before:h-full min-h-[44px]" />
          </ScrollAreaScrollbar>
        </ScrollAreaRoot>
      </DropdownMenuContent>
    </DropdownMenu>
  </div>
</template>